import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountGateBypass } from '@/hooks/useAccountGateBypass'
import { toast } from 'react-hot-toast'


export const usePasswordChangeCheck = () => {
  const { user, isAuthenticated } = useAuth()
  // #1344 — an impersonating admin must never be blocked by the target
  // account's forced-password obligation (they cannot fulfil it anyway).
  const { shouldBypassAccountGates } = useAccountGateBypass()
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [isChecking] = useState(false)

  // Handle API error events for force password change
  useEffect(() => {
    const handleApiError = (event: Event) => {
      const customEvent = event as CustomEvent
      const error = customEvent.detail

      // Check if this is a force password change error
      if (error?.status === 403 && error?.code === 'FORCE_PASSWORD_CHANGE') {
        setShowPasswordModal(true)
      }
    }

    // Listen for custom API error events
    window.addEventListener('api-error', handleApiError)

    return () => {
      window.removeEventListener('api-error', handleApiError)
    }
  }, [])

  useEffect(() => {
    const checkPasswordChangeRequired = () => {
      if (!isAuthenticated || !user) return

      // Check if user has force_password_change flag from JWT token (set during login)
      if (user.forcePasswordChange) {
        setShowPasswordModal(true)
        return
      }
    }

    checkPasswordChangeRequired()
  }, [isAuthenticated, user])

  const handlePasswordChangeSuccess = async () => {
    setShowPasswordModal(false)

    // NOTE: do NOT toast here. PasswordChangeModal already fires the success
    // toast (`success.passwordChanged`) before invoking onSuccess; adding one
    // here produced a duplicate "password changed" toast on the forced-change
    // flow. The modal also updates the token (dropping forcePasswordChange).
  }

  const handlePasswordChangeCancel = () => {
    // For forced password changes, we shouldn't allow canceling
    // But we can add logic here if needed
    if (user?.forcePasswordChange) {
      toast.error('Password change is required to continue')
      return
    }
    setShowPasswordModal(false)
  }

  // Masked at the output rather than at each trigger so the bypass also
  // covers the api-error listener (registered once, on mount) and a session
  // that flips into impersonation while the modal is already open.
  const gated = !shouldBypassAccountGates
  return {
    showPasswordModal: gated && showPasswordModal,
    setShowPasswordModal,
    isChecking,
    handlePasswordChangeSuccess,
    handlePasswordChangeCancel,
    isForced: gated && (user?.forcePasswordChange || false)
  }
}
